// Control & Admin > Language: the LIVE OVERRIDE LAYER. GET /api/i18n/overrides ({ version, overrides: { lang: { ns: { key: text } } } }) is merged into the running i18next store so an edit
// shows without a deploy. Fail-safe by construction: a failed fetch keeps the base texts and never throws into the app.
// Dotted keys are nested paths in the catalogs, so each text is written with i18n.addResource (setPath on the nested path); a flat addResourceBundle({ 'a.b': x }) would be shadowed by the nested 'a' object.
// The base text of every overridden key is remembered, so a reverted override restores it (an empty string = fall back to English, returnEmptyString is false).
const baseOf = new Map();   // 'lng|ns|key' -> text before the first override (undefined = the catalog had none)
let applied = new Map();    // 'lng|ns|key' -> override text currently in the store
let lastVersion = null;
let lastPayload = null;     // 47110: re-applied when a language's catalogs arrive later (each language is a lazy chunk)
let timer = null;

const parts = (k) => { const [lng, ns, ...rest] = k.split('|'); return [lng, ns, rest.join('|')]; };

export function applyOverrides(i18n, payload) {
  lastPayload = payload;
  const next = new Map();
  for (const [lng, byNs] of Object.entries((payload && payload.overrides) || {})) for (const [ns, byKey] of Object.entries(byNs)) for (const [key, text] of Object.entries(byKey)) {
    if (typeof text !== 'string' || !text) continue;
    if (!i18n.hasResourceBundle(lng, ns)) continue;   // 47110: that catalog is not loaded (yet): applied when it arrives ('catalogLoaded'), never fetched for an override
    const k = `${lng}|${ns}|${key}`;
    if (!baseOf.has(k)) baseOf.set(k, i18n.getResource(lng, ns, key));
    i18n.addResource(lng, ns, key, text, { silent: true });
    next.set(k, text);
  }
  let changed = next.size !== applied.size;
  for (const [k, v] of next) if (applied.get(k) !== v) changed = true;
  for (const k of applied.keys()) if (!next.has(k)) {   // reverted: put the base text back
    const [lng, ns, key] = parts(k); const base = baseOf.get(k);
    i18n.addResource(lng, ns, key, typeof base === 'string' ? base : '', { silent: true }); baseOf.delete(k);
  }
  applied = next;
  if (changed && i18n.language) i18n.emit('languageChanged', i18n.language);   // react-i18next re-renders on languageChanged; 47110: an event, not changeLanguage(), so it never overtakes a switch in flight
  return changed;
}

export async function refreshOverrides(api, i18n) {
  try {
    const p = await api.getI18nOverrides();
    if (!p || p.version === lastVersion) return false;
    lastVersion = p.version; return applyOverrides(i18n, p);
  } catch (e) { return false; }   // 503 / network / old API without the route: keep the base texts
}

// start once after sign-in; re-checks when the tab becomes visible again and every 5 minutes (a changed version re-applies)
export function startLiveOverrides(api, i18n) {
  refreshOverrides(api, i18n);
  if (timer || typeof document === 'undefined') return;
  i18n.on('catalogLoaded', () => { if (lastPayload) applyOverrides(i18n, lastPayload); });   // 47110: base text = the shipped one, so an override lands after its catalog
  const again = () => { if (document.visibilityState === 'visible') refreshOverrides(api, i18n); };
  document.addEventListener('visibilitychange', again);
  timer = setInterval(again, 5 * 60 * 1000);
}

// the catalogs as shipped (flat), with the overridden keys restored to their base text: what "base" means on the Language page
export function pristineCatalog(resources, flat) {
  const out = { en: {}, fr: {} };
  for (const lng of ['en', 'fr']) for (const ns of Object.keys(resources[lng] || {})) out[lng][ns] = flat(resources[lng][ns]);
  for (const [k, base] of baseOf) { const [lng, ns, key] = parts(k); if (!out[lng]) continue; if (typeof base === 'string') (out[lng][ns] ||= {})[key] = base; else if (out[lng][ns]) delete out[lng][ns][key]; }
  return out;
}
export const _reset = () => { baseOf.clear(); applied = new Map(); lastVersion = null; lastPayload = null; };
